import { parseJsonObject, readString } from './json';

/**
 * The `strokes` topic's shared bounds (Plan-04 §2) - enforced on BOTH ends:
 * the console decimates/caps before publishing, the headset defends itself
 * regardless (drop, count, surface in health - RA-D4).
 */
export const StrokeBounds = {
  /** Max points per stroke (console decimates with RDP before `end`). */
  maxPointsPerStroke: 256,
  /** Max live strokes rendered - oldest evicted beyond this. */
  maxLiveStrokes: 16,
  /** Serialized append-batch cap in bytes. */
  maxAppendBytes: 1024,
  /** Per-sender message rate cap (msgs/s), enforced defensively here. */
  maxMessagesPerSenderPerSecond: 30,
  /** Committed strokes persist this long (RA-D3 default; host-config). */
  defaultTtlSeconds: 10,
} as const;

/** The stroke lifecycle phases on the wire (Plan-04 §2). */
export type StrokePhase = 'begin' | 'append' | 'end' | 'erase' | 'clear';

/** A wire-space (or, after conversion, viewport-space) point. */
export interface StrokePoint {
  readonly x: number;
  readonly y: number;
}

/**
 * One parsed `strokes`-topic message (Plan-04 §2), field-for-field. Port of
 * `StrokeMessages.cs`:
 *
 *   begin  -> { "type":"stroke","phase":"begin","strokeId","sender","ptsRef"?, "color","width","points":[{x,y}] }
 *   append -> { "type":"stroke","phase":"append","strokeId","seq","points":[...] }
 *   end    -> { "type":"stroke","phase":"end","strokeId","ptsRef"?, "points":[complete <=256] }
 *   erase  -> { "type":"stroke","phase":"erase","strokeId" }
 *   clear  -> { "type":"stroke","phase":"clear","scope":"mine"|"all","sender"? }
 *
 * Points stay in the WIRE convention here - frame-normalized 0-1, y-down, the
 * bbox convention. The stroke service converts to viewport space (the same
 * invert flags the detection path applies) before anything renders.
 */
export interface StrokeMessage {
  readonly phase: StrokePhase;
  readonly strokeId: string;
  /** Embedded sender field (begin / clear-mine) - informative; the TRANSPORT identity is the attribution truth (RA-D2). */
  readonly sender: string;
  /** Media timestamp (RTP 90 kHz) of the frame drawn on; null = absent (RA-D6 fallback). */
  readonly ptsRef: number | null;
  /** "auto" (derive from sender hash) or "#rrggbb". */
  readonly color: string;
  /** Stroke width normalized to frame height. */
  readonly width: number;
  /** Append ordering/loss detection. */
  readonly seq: number;
  /** "mine" or "all" for clear. */
  readonly scope: string;
  /** Points (0-1, wire y-down until converted), clamped, capped at the §2 bound. Mutable so the service can convert in place. */
  points: StrokePoint[];
}

/** The wearer's clear-all broadcast ("Clear drawings", §3.2). */
export function clearAllStrokesJson(): string {
  return '{"type":"stroke","phase":"clear","scope":"all"}';
}

function readFiniteNumber(root: Record<string, unknown>, key: string): number | null {
  const value = root[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Individually malformed points are dropped, in-range ones clamped, and the list capped at the §2 bound - never fatal. */
function readPoints(root: Record<string, unknown>): StrokePoint[] {
  const points: StrokePoint[] = [];
  const array = root['points'];
  if (!Array.isArray(array)) return points;
  for (const entry of array) {
    if (points.length >= StrokeBounds.maxPointsPerStroke) break;
    if (typeof entry !== 'object' || entry === null) continue;
    const x = (entry as Record<string, unknown>)['x'];
    const y = (entry as Record<string, unknown>)['y'];
    if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) continue;
    points.push({ x: clamp01(x), y: clamp01(y) });
  }
  return points;
}

/**
 * Parse one strokes-topic payload. Null for anything malformed - the caller
 * logs-and-skips and counts the drop (the server rule; a bad message must
 * never tear rendering down).
 */
export function parseStrokeMessage(json: string): StrokeMessage | null {
  const root = parseJsonObject(json);
  if (!root || readString(root, 'type') !== 'stroke') return null;

  const strokeId = readString(root, 'strokeId');
  const base = {
    strokeId,
    sender: '',
    ptsRef: null as number | null,
    color: 'auto',
    width: 0.006,
    seq: 0,
    scope: '',
    points: [] as StrokePoint[],
  };

  switch (readString(root, 'phase')) {
    case 'begin': {
      const sender = typeof root['sender'] === 'string' ? (root['sender'] as string) : null;
      if (!strokeId || sender === null) return null;
      const width = readFiniteNumber(root, 'width') ?? 0.006;
      const color = readString(root, 'color');
      return {
        ...base,
        phase: 'begin',
        sender,
        ptsRef: readPtsRef(root),
        color: color || 'auto',
        width: Math.min(0.05, Math.max(0.001, width)),
        points: readPoints(root),
      };
    }
    case 'append': {
      const seq = readFiniteNumber(root, 'seq');
      if (!strokeId || seq === null || seq < 0) return null;
      return { ...base, phase: 'append', seq: Math.floor(seq), points: readPoints(root) };
    }
    case 'end':
      if (!strokeId) return null;
      return { ...base, phase: 'end', ptsRef: readPtsRef(root), points: readPoints(root) };
    case 'erase':
      if (!strokeId) return null;
      return { ...base, phase: 'erase' };
    case 'clear': {
      const scope = readString(root, 'scope');
      if (scope !== 'mine' && scope !== 'all') return null;
      return { ...base, phase: 'clear', scope, sender: readString(root, 'sender') };
    }
    default:
      return null;
  }
}

function readPtsRef(root: Record<string, unknown>): number | null {
  const value = readFiniteNumber(root, 'ptsRef');
  return value === null ? null : Math.trunc(value);
}

/**
 * Best-effort `pts` extraction from a detections payload WITHOUT a full JSON
 * parse - the stroke service watches the detections topic to map `ptsRef` ->
 * local arrival time, and the detection service already pays for the real
 * parse. Null when absent/unusable.
 */
export function tryExtractPts(json: string): number | null {
  if (!json) return null;
  const match = /"pts"\s*:\s*(-?\d+)/.exec(json);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isSafeInteger(value) ? value : null;
}
